import {
  ColDef,
  ColGroupDef,
  GridOptions,
  ICellRendererParams,
  ValueGetterParams,
  ValueSetterParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import { intl } from '../../../i18n'
import { formatDateTime } from '../../../utils/date'
import { IconCellRenderer } from './components/IconCellRenderer'
import { IconCellEditor } from './components/IconCellEditor'
import { NameCellRenderer } from './components/NameCellRenderer'
import { useRowNumber } from '../../page-properties/bulksheet-properties/rowNumber'
import { ClientSideTextFilter } from '../../containers/BulkSheetView/components/filter'
import { TicketTypeRow } from './TicketTypeRow'
import UserIconCellRenderer from '../../containers/commons/AgGrid/components/cell/common/iconCell'
import { ColumnType } from '../../containers/commons/AgGrid'
import { dragAndDrop } from '../../page-properties/bulksheet-properties/dragAndDrop'
/**
 * 使用中フィールドを○で表示するレンダラー（trueの時のみ）
 */
const IsUsedRenderer = (params: ICellRendererParams) => {
  const value = params.value
  return value ? '〇' : ''
}

/**
 * チケットタイプ管理用のグリッドオプションを生成
 */
export const useTicketTypesGridOptions = (): GridOptions => {
  const rowNumberColDef = useRowNumber()
  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      ...dragAndDrop,
      sortable: false,
      width: 30,
      maxWidth: 30,
    },
    {
      ...rowNumberColDef,
      headerName: '#',
    },
    {
      headerName: intl.formatMessage({
        id: 'ticketType.basicInfo',
      }),
      children: [
        {
          headerName: intl.formatMessage({
            id: 'ticketType.code',
          }),
          field: 'code',
          width: 150,
          hide: false,
          editable: params => {
            // プリセット行は編集不可
            if (params.data?.isPreset) return false
            // 新規追加行のみ編集可能（isAddedフラグがtrueの場合）
            return params.data?.isAdded === true
          },
          valueSetter: (params: ValueSetterParams<TicketTypeRow>) => {
            if (params.data) {
              params.data.code = params.newValue
              return true
            }
            return false
          },
          sortable: true,
          filter: ClientSideTextFilter,
          floatingFilter: true,
          resizable: true,
        },
        {
          headerName: intl.formatMessage({
            id: 'ticketType.name',
          }),
          field: 'name',
          width: 200,
          editable: params => !params.data?.isPreset, // プリセットの場合は編集不可
          valueSetter: (params: ValueSetterParams<TicketTypeRow>) => {
            if (params.data) {
              params.data.name = params.newValue
              return true
            }
            return false
          },
          cellRenderer: NameCellRenderer,
          sortable: true,
          filter: ClientSideTextFilter,
          floatingFilter: true,
          resizable: true,
        },
        {
          headerName: intl.formatMessage({
            id: 'ticketType.icon',
          }),
          field: 'iconUrl',
          width: 100,
          editable: params => !params.data?.isPreset, // プリセットの場合は編集不可
          valueSetter: (params: ValueSetterParams<TicketTypeRow>) => {
            if (params.data) {
              params.data.iconUrl = params.newValue
              return true
            }
            return false
          },
          cellRenderer: IconCellRenderer,
          cellEditor: IconCellEditor,
          suppressPaste: true,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          sortable: true,
          resizable: true,
        },
        {
          headerName: intl.formatMessage({
            id: 'ticketType.isUsed',
          }),
          field: 'isUsed',
          width: 100,
          editable: false,
          cellRenderer: IsUsedRenderer,
          cellStyle: { textAlign: 'center' },
          sortable: true,
          resizable: true,
        },
      ],
    },
    // 更新履歴列
    {
      headerName: intl.formatMessage({ id: 'changeLog' }),
      children: [
        {
          field: 'createdBy',
          headerName: intl.formatMessage({ id: 'createdBy' }),
          hide: true,
          width: 110,
          valueGetter: (params: ValueGetterParams) => {
            return params.data.createdBy?.name
          },
          cellRenderer: UserIconCellRenderer,
          cellRendererParams: {
            labelField: 'createdBy.name',
            iconUrlField: 'createdBy.iconUrl',
          },
        },
        {
          field: 'createdAt',
          headerName: intl.formatMessage({ id: 'createdAt' }),
          hide: true,
          width: 120,
          type: [ColumnType.dateTime],
          valueFormatter: (params: ValueFormatterParams) =>
            formatDateTime(params.value) || '',
        },
        {
          field: 'updatedBy',
          headerName: intl.formatMessage({ id: 'updatedBy' }),
          hide: true,
          width: 110,
          valueGetter: (params: ValueGetterParams) => {
            return params.data.updatedBy?.name
          },
          cellRenderer: UserIconCellRenderer,
          cellRendererParams: {
            labelField: 'updatedBy.name',
            iconUrlField: 'updatedBy.iconUrl',
          },
        },
        {
          field: 'updatedAt',
          headerName: intl.formatMessage({ id: 'updatedAt' }),
          hide: true,
          width: 120,
          type: [ColumnType.dateTime],
          valueFormatter: (params: ValueFormatterParams) =>
            formatDateTime(params.value) || '',
        },
      ],
    },
  ]

  return {
    columnDefs,
    context: {},
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: true, // 列ヘッダーのハンバーガーメニューを非表示
      cellEditor: 'textEditor', // 標準のテキストエディターを使用
      singleClickEdit: false, // ダブルクリックで編集開始
    },
    enableRangeSelection: true,
    rowSelection: 'multiple',
    suppressRowClickSelection: false,
    getRowId: params => params.data.uuid,
    allowContextMenuWithControlKey: true,
    preventDefaultOnContextMenu: true,
    // ドラッグ&ドロップ設定
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    onRowDoubleClicked: () => {
      // ダブルクリック時の遷移を無効化
      return false
    },
  }
}
